import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import dashboardService from '../services/dashboardService';
import { useAuth } from './AuthContext';

const DashboardContext = createContext();

export const useDashboard = () => useContext(DashboardContext);

export const DashboardProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    console.log('DashboardContext: Starting fetchDashboardData...');
    setLoading(true);
    setError(null);
    try {
      console.log('DashboardContext: Calling dashboardService.getDashboardData()...');
      const data = await dashboardService.getDashboardData();
      console.log('DashboardContext: Received data from service:', data);
      setDashboardData(data);
      console.log('DashboardContext: Successfully set dashboard data');
    } catch (err) {
      console.error('DashboardContext: Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      console.error('DashboardContext: Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data
      });
    } finally {
      setLoading(false);
      console.log('DashboardContext: fetchDashboardData completed, loading set to false');
    }
  }, []);

  const fetchTaskCompletionHistory = useCallback(async (days = 30) => {
    setLoading(true);
    setError(null);
    try {
      const history = await dashboardService.getTaskCompletionHistory(days);
      return history;
    } catch (err) {
      console.error('Error fetching task completion history:', err);
      setError('Failed to load task completion history. Please try again.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTasksCompletedPerDay = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getTasksCompletedPerDay(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching tasks completed per day:', err);
      setError('Failed to load tasks completed per day data. Please try again.');
      return [];
    }
  }, []);

  const fetchTaskDurationByCategory = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getTaskDurationByCategory(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching task duration by category:', err);
      setError('Failed to load task duration by category data. Please try again.');
      return [];
    }
  }, []);

  const fetchTaskCompletionTimeliness = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getTaskCompletionTimeliness(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching task completion timeliness:', err);
      setError('Failed to load task completion timeliness data. Please try again.');
      return null;
    }
  }, []);

  const fetchCompletionStatusOverTime = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getCompletionStatusOverTime(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching completion status over time:', err);
      setError('Failed to load completion status over time data. Please try again.');
      return [];
    }
  }, []);

  const fetchAverageDurationPerDay = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getAverageDurationPerDay(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching average duration per day:', err);
      setError('Failed to load average duration per day data. Please try again.');
      return [];
    }
  }, []);

  const fetchTasksByHour = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getTasksByHour(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching tasks by hour:', err);
      setError('Failed to load tasks by hour data. Please try again.');
      return [];
    }
  }, []);

  const fetchCompletionHeatmap = useCallback(async (startDate, endDate) => {
    try {
      const data = await dashboardService.getCompletionHeatmap(startDate, endDate);
      return data;
    } catch (err) {
      console.error('Error fetching completion heatmap:', err);
      setError('Failed to load completion heatmap data. Please try again.');
      return null;
    }
  }, []);

  // Load dashboard data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    } else {
      // Clear data when not authenticated
      setDashboardData(null);
    }
  }, [isAuthenticated, fetchDashboardData]);

  const value = {
    dashboardData,
    loading,
    error,
    fetchDashboardData,
    fetchTaskCompletionHistory,
    fetchTasksCompletedPerDay,
    fetchTaskDurationByCategory,
    fetchTaskCompletionTimeliness,
    fetchCompletionStatusOverTime,
    fetchAverageDurationPerDay,
    fetchTasksByHour,
    fetchCompletionHeatmap
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext; 